///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Signal, signal, WritableSignal } from '@angular/core';
import { ProjectFolderDTO, ProjectFolderEnum } from '../../generated/shared-types.model';
import { Subject } from 'rxjs';
import { isDefined } from '@siklu/sui-lib/utils';

export type FolderTreeNode = ProjectFolderDTO & {
    isRoot: WritableSignal<boolean>;
    isOpened: WritableSignal<boolean>;
    isSelected: WritableSignal<boolean>;
    hasChildren: WritableSignal<boolean>;
    children: FolderTreeNode[];
};
export type FolderTreeNodeInfo = Pick<ProjectFolderDTO, 'id' | 'name'>;

export class FoldersTreeService {
    private nodeMap: Record<number, FolderTreeNode> = {};

    private readonly tree: WritableSignal<FolderTreeNode[]> = signal([]);
    private readonly activePath: WritableSignal<FolderTreeNodeInfo[]> = signal([]);
    private readonly selectedFolder: WritableSignal<FolderTreeNode> = signal(null);

    private readonly _selectionChanges$ = new Subject<number>();
    public readonly selectionChanges$ = this._selectionChanges$.asObservable();

    public createTree(folders: ProjectFolderDTO[]): void {
        this.buildTree(folders);
    }

    public isTreeReady(): boolean {
        return this.tree().length > 0;
    }

    public expandAll(): void {
        for (const folderId in this.nodeMap) {
            this.nodeMap[folderId].isOpened.set(true);
        }
    }

    public collapseAll(): void {
        for (const folderId in this.nodeMap) {
            this.nodeMap[folderId].isOpened.set(false);
        }
    }

    public getDefaultRootFolderId(): number | undefined {
        const rootFolder = this.tree().find((folder: FolderTreeNode) => folder.type === ProjectFolderEnum.PROJECT);
        return rootFolder?.id ?? this.tree().at(0)?.id;
    }

    public getFolder(folderId: number): FolderTreeNode | undefined {
        return this.nodeMap[folderId];
    }

    public getTree(): Signal<FolderTreeNode[]> {
        return this.tree.asReadonly();
    }

    public getSelectedFolder(): Signal<FolderTreeNode> {
        return this.selectedFolder.asReadonly();
    }

    public getActivePath(): Signal<FolderTreeNodeInfo[]> {
        return this.activePath.asReadonly();
    }

    public selectRootFolder(): boolean {
        return this.selectFolder(this.getDefaultRootFolderId());
    }

    public selectFolder(folderId: number): boolean {
        if (this.isFolderSelected(folderId)) {
            return true;
        }

        this.clearSelection();

        if (this.hasFolder(folderId)) {
            const path: FolderTreeNode[] = this.findActivePath(folderId);
            this.activePath.set(path);
            this.selectedFolder.set(path.at(-1));
            this._selectionChanges$.next(folderId);
            return true;
        }

        return false;
    }

    public toggleOpen(folderId: number): void {
        const folder = this.nodeMap[folderId];
        folder.isOpened.set(!folder.isOpened());
    }

    public clearTree() {
        this.tree.set([]);
        this.activePath.set([]);
        this.selectedFolder.set(null);
    }

    private findActivePath(folderId: number): FolderTreeNode[] {
        for (const root of this.tree()) {
            const path = this.findPath(root, folderId);
            if (path.length) {
                return path;
            }
        }
    }

    private readonly findPath = (folder: FolderTreeNode, folderId: number): FolderTreeNode[] => {
        if (folder.id === folderId) {
            folder.isSelected.set(true);
            return [folder];
        }
        for (const child of folder.children) {
            const path: FolderTreeNode[] = this.findPath(child, folderId);
            if (path.length) {
                folder.isOpened.set(true);
                folder.isSelected.set(true);
                path.unshift(folder);
                return path;
            }
        }
        return [];
    };

    private buildTree(folders: ProjectFolderDTO[]): void {
        const nodeMap = {};
        folders.forEach((folder: ProjectFolderDTO): void => {
            const isOpened = this.isFolderOpened(folder.id);
            nodeMap[folder.id] = {
                ...folder,
                isRoot: signal(false),
                isOpened: signal(isOpened),
                isSelected: signal(false),
                hasChildren: signal(false),
                children: []
            };
        });

        const tree = [];
        folders.forEach((folder: ProjectFolderDTO): void => {
            const node = nodeMap[folder.id];
            const parentId = folder.parentId;

            node.isRoot.set(!isDefined(parentId));

            if (node.isRoot()) {
                tree.push(node);
                if (node.type === ProjectFolderEnum.PROJECT) {
                    node.isOpened.set(true);
                }
            } else {
                const parent: FolderTreeNode = nodeMap[parentId];
                parent.children.push(node);
                parent.hasChildren.set(true);
            }
        });

        this.clearTree();

        this.nodeMap = nodeMap;
        this.tree.set(tree);
    }

    private hasFolder(folderId: number): boolean {
        return isDefined(this.nodeMap[folderId]);
    }

    private isFolderSelected(folderId: number): boolean {
        const selectedFolder = this.selectedFolder();
        return isDefined(selectedFolder) && selectedFolder.id === folderId;
    }

    private isFolderOpened(folderId: number): boolean {
        return this.nodeMap[folderId]?.isOpened() ?? false;
    }

    private clearSelection() {
        this.activePath.set([]);
        this.selectedFolder.set(null);
        for (const folderId in this.nodeMap) {
            this.nodeMap[folderId].isSelected.set(false);
        }
    }
}
